import React from 'react';
import { Card } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Area, AreaChart } from 'recharts';
import { AlertTriangle, Zap, Clock, Shield } from 'lucide-react';

export const VelocityAttackMonitor: React.FC = () => {
  const velocityData = [
    { time: '14:20', submissions: 1, threshold: 5 },
    { time: '14:21', submissions: 2, threshold: 5 },
    { time: '14:22', submissions: 1, threshold: 5 },
    { time: '14:23', submissions: 5, threshold: 5 },
    { time: '14:24', submissions: 7, threshold: 5 },
    { time: '14:25', submissions: 4, threshold: 5 },
    { time: '14:26', submissions: 2, threshold: 5 },
    { time: '14:27', submissions: 1, threshold: 5 },
    { time: '14:28', submissions: 3, threshold: 5 },
    { time: '14:29', submissions: 6, threshold: 5 }
  ];

  const recentAttacks = [
    { 
      id: 1,
      timeRange: '14:23 - 14:26', 
      leads: 7, 
      duration: '3 min', 
      status: 'blocked',
      source: 'IP: 169.197.85.172',
      pattern: 'Sequential forms'
    },
    { 
      id: 2,
      timeRange: '13:45 - 13:48', 
      leads: 5, 
      duration: '3 min', 
      status: 'blocked',
      source: 'IP: 207.244.89.166',
      pattern: 'Bot submission'
    },
    { 
      id: 3,
      timeRange: '12:15 - 12:17', 
      leads: 4, 
      duration: '2 min', 
      status: 'investigating',
      source: 'IP: 185.220.102.8',
      pattern: 'Unusual speed'
    }
  ];

  const stats = {
    totalAttacks: recentAttacks.length,
    blockedAttacks: recentAttacks.filter(a => a.status === 'blocked').length,
    avgDuration: recentAttacks.reduce((sum, a) => sum + parseInt(a.duration), 0) / recentAttacks.length,
    totalPrevented: recentAttacks.reduce((sum, a) => sum + a.leads, 0)
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'blocked':
        return 'bg-red-100 text-red-800';
      case 'investigating':
        return 'bg-yellow-100 text-yellow-800';
      case 'contained':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'blocked':
        return <Shield className="w-4 h-4 text-red-500" />;
      case 'investigating':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'contained':
        return <Shield className="w-4 h-4 text-green-500" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-gray-500" />;
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const isAboveThreshold = data.submissions > data.threshold;
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="text-sm font-medium">{label}</p>
          <p className="text-sm">Submissions: {data.submissions}</p>
          <p className="text-sm">Threshold: {data.threshold}</p>
          {isAboveThreshold && (
            <p className="text-sm text-red-600 font-medium">⚠️ Above threshold!</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <Zap className="w-6 h-6 text-orange-500" />
        <div>
          <h3 className="text-lg font-semibold text-foreground">Velocity Attack Monitor</h3>
          <p className="text-sm text-muted-foreground">Real-time submission rate analysis</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-sm text-muted-foreground">Monitoring</span>
        </div>
      </div>

      {/* Real-time Chart */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-foreground">Submission Rate (Last 10 minutes)</h4>
          <div className="text-sm text-muted-foreground">
            Threshold: <span className="font-medium text-red-600">5 submissions/minute</span>
          </div>
        </div>
        
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={velocityData}>
            <defs>
              <linearGradient id="submissionGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <XAxis dataKey="time" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip content={<CustomTooltip />} />
            <Area 
              type="monotone" 
              dataKey="submissions" 
              stroke="#f59e0b" 
              strokeWidth={2}
              fill="url(#submissionGradient)"
            />
            <Line 
              type="monotone" 
              dataKey="threshold" 
              stroke="#ef4444" 
              strokeWidth={2} 
              strokeDasharray="5,5"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Recent Attacks */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-foreground">Recent Velocity Attacks</h4>
          <span className="text-sm text-muted-foreground">{stats.totalAttacks} detected today</span>
        </div>
        
        <div className="space-y-3">
          {recentAttacks.map((attack) => (
            <div key={attack.id} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg border">
              <div className="flex items-start gap-3">
                {getStatusIcon(attack.status)}
                <div>
                  <div className="font-medium text-sm text-foreground">{attack.timeRange}</div>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <div>{attack.source}</div>
                    <div>Pattern: {attack.pattern}</div>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-foreground">
                  {attack.leads} leads in {attack.duration}
                </div>
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${getStatusColor(attack.status)}`}>
                  {attack.status.toUpperCase()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-4 gap-4 mt-6 pt-4 border-t">
        <div className="text-center">
          <div className="text-2xl font-bold text-orange-600">{stats.totalAttacks}</div>
          <div className="text-xs text-muted-foreground">Total Attacks</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">{stats.blockedAttacks}</div>
          <div className="text-xs text-muted-foreground">Blocked</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{stats.avgDuration.toFixed(1)}m</div>
          <div className="text-xs text-muted-foreground">Avg Duration</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{stats.totalPrevented}</div>
          <div className="text-xs text-muted-foreground">Leads Prevented</div>
        </div>
      </div>
    </Card>
  );
};